import React from 'react';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  skeleton: {
    background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
    backgroundSize: '200% 100%',
    animation: 'loading 1.5s infinite',
    borderRadius: theme.shape.borderRadius,
  },
  '@keyframes loading': {
    '0%': {
      backgroundPosition: '200% 0',
    },
    '100%': {
      backgroundPosition: '-200% 0',
    },
  },
  container: {
    padding: theme.spacing(2),
  },
  row: {
    display: 'flex',
    marginBottom: theme.spacing(1),
    alignItems: 'center',
  },
  text: {
    height: '1.2em',
    marginBottom: theme.spacing(0.5),
  },
  title: {
    height: '1.5em',
    width: '60%',
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    height: '1em',
    width: '40%',
    marginBottom: theme.spacing(2),
  },
  card: {
    padding: theme.spacing(2),
    border: `1px solid #e0e0e0`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  button: {
    height: '36px',
    width: '120px',
    borderRadius: theme.shape.borderRadius,
  },
  avatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    marginRight: theme.spacing(1),
  },
}));

const SkeletonElement = ({ variant = 'text', width = '100%', height = '1em', className = '' }) => {
  const classes = useStyles();
  
  const baseClasses = `${classes.skeleton} ${className}`;
  
  const styles = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
  };

  return <div className={baseClasses} style={styles} />;
};

export const SkeletonCard = ({ lines = 3, showAvatar = false }) => {
  const classes = useStyles();

  return (
    <div className={classes.card}>
      {showAvatar && (
        <div className={classes.row}>
          <SkeletonElement variant="circle" width={40} height={40} className={classes.avatar} />
          <SkeletonElement width="60%" height="1.2em" />
        </div>
      )}
      <SkeletonElement className={classes.title} />
      {Array.from({ length: lines }).map((_, index) => (
        <SkeletonElement 
          key={index} 
          width={index === lines - 1 ? '80%' : '100%'} 
          className={classes.text}
        />
      ))}
    </div>
  );
};

export const SkeletonTable = ({ rows = 5, columns = 4 }) => {
  const classes = useStyles();

  return (
    <div className={classes.container}>
      <div className={classes.row}>
        {Array.from({ length: columns }).map((_, index) => (
          <SkeletonElement 
            key={`header-${index}`} 
            width="120px" 
            height="1.2em" 
            style={{ marginRight: 16 }}
          />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div className={classes.row} key={`row-${rowIndex}`}>
          {Array.from({ length: columns }).map((_, colIndex) => (
            <SkeletonElement 
              key={`cell-${rowIndex}-${colIndex}`} 
              width={colIndex === 0 ? '150px' : '100px'} 
              height="1em" 
              style={{ marginRight: 16 }}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

export const SkeletonProjectSummary = () => {
  const classes = useStyles();

  return (
    <div className={classes.container}>
      {/* Header */}
      <div className={classes.row}>
        <SkeletonElement className={classes.title} />
      </div>
      
      {/* Project Cards */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        {[1, 2, 3, 4].map((item) => (
          <SkeletonCard key={item} lines={2} />
        ))}
      </div>
      
      {/* Overview Section */}
      <SkeletonCard lines={4} />
      
      {/* Table */}
      <SkeletonTable rows={5} columns={6} />
    </div>
  );
};

export const SkeletonList = ({ items = 5 }) => {
  return (
    <div>
      {Array.from({ length: items }).map((_, index) => (
        <SkeletonCard key={index} lines={2} showAvatar={index % 2 === 0} />
      ))}
    </div>
  );
};

export default SkeletonElement;
